

import React, { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { v2 as t } from "styles/tokens/v2";
import { MONO_FONT_CLASS } from "styles/tokens/typography";
import { useCurrency } from '../../context/CurrencyContext';
import { linkedValue } from "components/v2/ContactValue";

function InlineEditField({
  label,
  value,
  onSave,
  onError,
  readOnly = false,
  multiline = false,
  options = null,
  isEditing,
  isMultiSelect = false,
  currency = null,
  renderValue = null,
  required = false,
  is_required = false,
  isCheckbox = false,
  type = "text",
  error: externalError = '', // NEW: parent-supplied error (e.g. from a form-level validator)
}) {
  const [inputValue, setInputValue] = useState(value);
  const [localError, setLocalError] = useState('');
  const { formatCurrency } = useCurrency();
  const isV2 = useTheme()?.pearlUi === "v2";

  useEffect(() => {
    setInputValue(value);
    setLocalError('');
  }, [value]);

  const displayError = localError || externalError;

  const handleChange = (e, newValue) => {
    let updatedValue;

    if (isCheckbox) {
      updatedValue = e.target.checked;
    } else if (isMultiSelect) {
      updatedValue = newValue;
    } else if (options) {
      updatedValue = e.target.value;
    } else if (currency) {
      updatedValue = parseFloat(e.target.value);
    } else {
      updatedValue = e.target.value;
    }

    setInputValue(updatedValue);
    setLocalError('');
    onError && onError(label, '');
    onSave(updatedValue); // parent's own onSave (e.g. handleFieldUpdate) is
    // what clears externalError, by removing it from formErrors
  };

  const validateField = (label, value) => {
    if ((required || is_required) && !value) return 'This field is required';
    if (!value) return '';
    switch (label.toLowerCase()) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Please enter a valid email address';
      case 'phone':
        // Aligned with ContactDetail's validateContactForm regex — this used
        // to require exactly 10 digits and reject international/formatted
        // numbers that the form-level check accepted, giving contradictory
        // feedback (red on blur, Save succeeds anyway).
        return /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/.test(value)
            ? ''
            : 'Please enter a valid phone number';
      case 'website':
        return /^[a-zA-Z0-9][a-zA-Z0-9-._]*\.[a-zA-Z]{2,}$/i.test(value) ? '' : 'Please enter a valid website domain (e.g. example.com)';
      default:
        return '';
    }
  };

  const handleBlur = () => {
    const validationError = validateField(label, inputValue);
    setLocalError(validationError);
    onError && onError(label, validationError);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleBlur();
    }
  };

  if (isEditing && !readOnly) {
    if (currency) {
      return (
        <TextField
          fullWidth
          label={label}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          error={!!displayError}
          helperText={displayError}
          type="number"
          InputProps={{
            startAdornment: <span>{currency}</span>,
          }}
          required={required}
        />
      );
    } else if (type === "date") {
      const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format

      return (
        <TextField
          fullWidth
          label={label}
          type="date"
          value={inputValue || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!displayError}
          helperText={displayError}
          InputLabelProps={{ shrink: true }}
          InputProps={{ inputProps: { min: today } }}
          sx={{ '& input[type="date"]::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
        />
      );
    } else if (type === "time") {
      return (
        <TextField
          fullWidth
          label={label}
          type="time"
          value={inputValue || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          error={!!displayError}
          helperText={displayError || 'HH:mm'}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{ '& input[type="time"]::-webkit-calendar-picker-indicator': { cursor: 'pointer' } }}
        />
      );
    } else if (isMultiSelect) {
      return (
        <Autocomplete
          multiple
          options={(options || []).map(option => option.label || option.value || option)}
          value={Array.isArray(inputValue) ? inputValue : []}
          onChange={handleChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              error={!!displayError}
              helperText={displayError}
              required={required}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={index}
                variant="outlined"
                label={option.label || option}
                {...getTagProps({ index })}
              />
            ))
          }
          onBlur={handleBlur}
        />
      );
    } else if (options) {
      return (
        <FormControl fullWidth error={!!displayError} required={required}>
          <InputLabel id={`inline-edit-${label}`}>{label}</InputLabel>
          <Select
            labelId={`inline-edit-${label}`}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            label={label}
            sx={{
              height: '45px',
              minHeight: '45px',
              '& .MuiSelect-select': {
                height: '45px',
                minHeight: '45px !important',
                display: 'flex',
                alignItems: 'center',
                paddingTop: 0,
                paddingBottom: 0,
                boxSizing: 'border-box',
              },
            }}
          >
            {(options || []).map((option) => (
              <MenuItem key={option.value || option} value={option.value || option}>
                {option.label || option}
              </MenuItem>
            ))}
          </Select>
          {displayError && <FormHelperText>{displayError}</FormHelperText>}
        </FormControl>
      );
    } else {
      return (
        <TextField
          fullWidth
          label={label}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          multiline={multiline}
          rows={multiline ? 4 : 1}
          error={!!displayError}
          helperText={displayError}
          required={required}
          type={type} // Dynamically set the type here
        />
      );
    }
  } else {
    let displayContent;
    if (renderValue) {
      displayContent = renderValue(value);
    } else if (currency) {
      // Improved currency handling with better null/undefined checks
      displayContent = value !== null && value !== undefined && !isNaN(parseFloat(value)) 
        ? formatCurrency(value, currency)
        : ' ';
    } else if (isMultiSelect && Array.isArray(value)) {
      displayContent = value.map(v => v.label || v).join(', ');
    } else if (options && !isMultiSelect) {
      const selectedOption = options.find(opt => opt.value === value || opt === value);
      displayContent = selectedOption ? (selectedOption.label || selectedOption) : value;
    } else {
      displayContent = value || "";
    }

    // v2 (design-reference "Sales · Deal record"): a small label above the
    // value, a hairline under each field, no monospace.
    if (isV2) {
      // Phone numbers call, emails and websites open (contactLinks.js). A
      // page formatter that returns plain text (e.g. capitalising) gives way
      // to the link, which shows the value as stored.
      if (!options && !currency && typeof value === "string" && typeof displayContent === "string") {
        displayContent = linkedValue(label, value, displayContent);
      }
      const empty = displayContent === null || displayContent === undefined || (typeof displayContent === "string" && !displayContent.trim());
      return (
        <MDBox sx={{ pb: 1.25, borderBottom: `1px solid ${t.border}`, minWidth: 0 }}>
          <MDTypography component="div" sx={{ fontSize: 12, lineHeight: 1.35, color: t.text2, mb: 0.5 }}>
            {label}
            {(required || is_required) ? ' *' : ''}
          </MDTypography>
          <MDTypography
            component="div"
            sx={{ fontSize: 14, lineHeight: 1.45, color: empty ? t.textDisabled : t.text, overflowWrap: "break-word", fontVariantNumeric: "tabular-nums" }}
          >
            {empty ? "—" : displayContent}
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <MDTypography variant="button" fontWeight="regular" color="text">
        <strong>{label}:</strong>{" "}
        <MDTypography
          component="span"
          variant="mono"
          className={MONO_FONT_CLASS}
          data-font="mono"
          fontWeight="regular"
          color="text"
        >
          {displayContent}
        </MDTypography>
      </MDTypography>
    );
  }
}

export default InlineEditField;
